import React, { useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useAlert } from './contexts/AlertContext';
import { useSettings } from './contexts/SettingsContext';
import apiService from './apiService';
import { Package, DollarSign, Users, TrendingUp, ShoppingCart, Check } from 'lucide-react';

const ModuleUpgrade = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const { appSettings } = useSettings();
    const [selectedModules, setSelectedModules] = useState([]);

    const availableModules = [
        { 
            name: 'enableStaffManagement', 
            display: 'Staff Management',
            price: 6,
            icon: Users,
            description: 'Manage doctors, nurses, and staff scheduling',
            disabled: appSettings.practiceSize === 'solo'
        },
        { 
            name: 'enableInventoryManagement', 
            display: 'Inventory & Pharmacy',
            price: 6,
            icon: Package,
            description: 'Full inventory tracking and pharmacy management',
            disabled: appSettings.practiceSize === 'solo' || appSettings.practiceSize === 'pharmacy'
        },
        { 
            name: 'enableReports', 
            display: 'Advanced Analytics & Reports',
            price: 6,
            icon: TrendingUp,
            description: 'Comprehensive reporting and business intelligence',
            disabled: appSettings.practiceSize === 'solo'
        },
        { 
            name: 'enablePOS', 
            display: 'Point of Sale System',
            price: 6,
            icon: ShoppingCart,
            description: 'Integrated billing and payment processing',
            disabled: false
        },
    ];

    const basePrices = {
        solo: 20,
        clinic: 40,
        hospital: 100,
        pharmacy: 35
    };

    const handleToggleModule = (moduleName) => {
        if (selectedModules.includes(moduleName)) {
            setSelectedModules(selectedModules.filter(m => m !== moduleName));
        } else {
            setSelectedModules([...selectedModules, moduleName]);
        }
    };

    const calculateTotal = () => {
        const basePrice = basePrices[appSettings.practiceSize] || 0;
        const modulesCost = availableModules
            .filter(m => selectedModules.includes(m.name))
            .reduce((sum, m) => sum + m.price, 0);
        return basePrice + modulesCost;
    };

    const handleRequestUpgrade = async () => {
        if (selectedModules.length === 0) {
            showAlert('Please select at least one module to upgrade', 'warning');
            return;
        }

        try {
            // API call to create upgrade request
            await apiService.createUpgradeRequest({
                modulesRequested: selectedModules,
                requestNotes: `Requesting modules: ${selectedModules.join(', ')}`
            });
            showAlert('Upgrade request submitted. An administrator will review it shortly.', 'success');
            setSelectedModules([]);
        } catch (e) {
            showAlert('Failed to submit upgrade request', 'error');
        }
    };

    if (!['org_admin'].includes(currentUser?.role)) {
        return <div className="p-6 text-red-600">Only organization administrators can request upgrades.</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold">Upgrade Modules</h2>
                <p className="text-gray-600 dark:text-gray-400">Expand your organization's capabilities</p>
            </div>

            {/* Current Plan */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm opacity-90">Current Plan</p>
                        <h3 className="text-2xl font-bold capitalize">{appSettings.practiceSize} Plan</h3>
                        <p className="text-sm mt-2">${basePrices[appSettings.practiceSize]}/month (USD)</p>
                    </div>
                    <DollarSign className="w-12 h-12 opacity-80" />
                </div>
            </div>

            {/* Available Modules */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableModules.map(module => {
                    const Icon = module.icon;
                    const isActive = appSettings[module.name];
                    const isSelected = selectedModules.includes(module.name);
                    const isDisabled = module.disabled || isActive;

                    return (
                        <div
                            key={module.name}
                            className={`border rounded-lg p-4 ${
                                isDisabled 
                                    ? 'bg-gray-100 dark:bg-gray-800 opacity-60' 
                                    : isSelected 
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                                    : 'bg-white dark:bg-gray-800 hover:border-blue-300'
                            } transition-all cursor-pointer`}
                            onClick={() => !isDisabled && handleToggleModule(module.name)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <Icon className="w-5 h-5 text-blue-600" />
                                        <h4 className="font-semibold">{module.display}</h4>
                                        {isActive && (
                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{module.description}</p>
                                    <p className="text-lg font-bold text-blue-600 mt-3">${module.price}/month</p>
                                </div>
                                {isSelected && !isDisabled && (
                                    <Check className="w-6 h-6 text-blue-600" />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Summary & Checkout */}
            {selectedModules.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Upgrade Summary</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span>Base Plan ({appSettings.practiceSize})</span>
                            <span>${basePrices[appSettings.practiceSize]}/month</span>
                        </div>
                        {availableModules
                            .filter(m => selectedModules.includes(m.name))
                            .map(m => (
                                <div key={m.name} className="flex justify-between text-blue-600">
                                    <span>+ {m.display}</span>
                                    <span>${m.price}/month</span>
                                </div>
                            ))}
                        <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                            <span>New Total</span>
                            <span>${calculateTotal()}/month</span>
                        </div>
                    </div>
                    <button
                        onClick={handleRequestUpgrade}
                        className="w-full mt-6 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                        <ShoppingCart className="w-5 h-5" />
                        Request Upgrade
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                        A payment link will be sent to your email after approval
                    </p>
                </div>
            )}
        </div>
    );
};

export default ModuleUpgrade;

